import { useQuery } from "@tanstack/react-query";
import { Capacitor } from "@capacitor/core";
import { Button } from "@/components/ui/button";
import { startAppleOAuthMobile, startGoogleOAuthMobile } from "@/lib/native";
import { useAppleSignInAvailable, useHideGoogleOnIos } from "@/hooks/useHideGoogleOnIos";

interface ProvidersResponse {
  google?: boolean;
  apple?: boolean;
}

export function OAuthButtons({ label }: { label: string }) {
  const { data } = useQuery<ProvidersResponse>({
    queryKey: ["/api/auth/providers"],
    queryFn: async () => {
      const res = await fetch("/api/auth/providers", { credentials: "include" });
      if (!res.ok) throw new Error("could not load providers");
      return res.json();
    },
    staleTime: 60_000,
  });

  const hideGoogle = useHideGoogleOnIos();
  const appleAvailable = useAppleSignInAvailable();

  const showGoogle = Boolean(data?.google) && !hideGoogle;
  const showApple = Boolean(data?.apple) && appleAvailable;
  if (!showGoogle && !showApple) return null;

  const handleGoogle = () => {
    if (Capacitor.isNativePlatform()) {
      void startGoogleOAuthMobile();
    } else {
      window.location.href = "/api/auth/google";
    }
  };

  return (
    <div className="space-y-3">
      {showApple && (
        <Button
          type="button"
          variant="outline"
          className="w-full justify-center gap-2"
          onClick={() => void startAppleOAuthMobile()}
        >
          <AppleIcon />
          {label} with Apple
        </Button>
      )}
      {showGoogle && (
        <Button
          type="button"
          variant="outline"
          className="w-full justify-center gap-2"
          onClick={handleGoogle}
        >
          <GoogleIcon />
          {label} with Google
        </Button>
      )}
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <div className="h-px flex-1 bg-border" />
        <span>or {label.toLowerCase()} with email</span>
        <div className="h-px flex-1 bg-border" />
      </div>
    </div>
  );
}

export function AppleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M17.05 12.54c.02-2.06 1.68-3.05 1.76-3.1-.96-1.4-2.45-1.6-2.98-1.62-1.27-.13-2.48.75-3.12.75-.64 0-1.64-.73-2.7-.71-1.39.02-2.67.81-3.38 2.05-1.44 2.5-.37 6.2 1.03 8.23.69 1 1.51 2.11 2.58 2.07 1.04-.04 1.43-.67 2.68-.67 1.25 0 1.6.67 2.7.65 1.11-.02 1.82-1.01 2.5-2.01.79-1.15 1.11-2.27 1.13-2.33-.02-.01-2.17-.83-2.2-3.31zM15.1 6.09c.56-.69.94-1.64.84-2.59-.81.03-1.8.54-2.38 1.22-.52.6-.98 1.57-.86 2.49.91.07 1.83-.46 2.4-1.12z" />
    </svg>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
      />
    </svg>
  );
}
